import moment, { min } from 'moment';

export const widgetTypes = [
    {
        id: "linearmeter",
        name: "Linear Meter",
        properties: {
            mix: 0,
            max: 100
        }
    },
    {
        id: "digitalmeter",
        name: "Digital Meter"
    },
    {
        id: "indicator",
        name: "Indicator"
    },
    {
        id: "uptimecounter",
        name: "Uptime Counter"
    },
    {
        id: "radialgauge",
        name: "Radial Gauge",
        properties: {
            mix: 0,
            max: 100
        }
    },
];

export const Indicator = ({ name, reading = false, lastupdated }) => {
    const activeColor = reading ? "#00ff41" : "#ff4d4d";
    const statusLabel = reading ? "ON / ACTIVE" : "OFF / IDLE";

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: '#151515',
            padding: '10px 15px',
            borderRadius: '8px',
            border: '1px solid #333',
            minWidth: '180px',
            boxShadow: '0 4px 10px rgba(0,0,0,0.4)'
        }}>
            {/* 1. The Physical LED Bulb */}
            <div style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                background: reading ? activeColor : '#222',
                border: `2px solid ${reading ? activeColor : '#444'}`,
                boxShadow: reading ? `0 0 12px ${activeColor}, inset 0 0 4px #fff` : 'none',
                transition: 'all 0.3s ease'
            }} />

            {/* 2. Status Information */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{
                    color: '#888',
                    fontSize: '9px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    fontWeight: 'bold'
                }}>
                    {name}
                </div>

                <div style={{
                    color: activeColor,
                    fontSize: '14px',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    marginTop: '2px'
                }}>
                    {statusLabel}
                </div>

                {lastupdated && (
                    <div style={{ color: '#444', fontSize: '8px', marginTop: '4px' }}>
                        SYNC: {lastupdated}
                    </div>
                )}
            </div>
        </div>
    );
};

export const renderProperties = (properties, values, onChange) => {
    if (!properties) return null;

    return Object.keys(properties).map((key) => (
        <div key={key}>
            <label>{key}</label>
            <input
                type="number"
                value={values?.[key] ?? properties[key]}
                onChange={(e) => onChange(key, e.target.value)}
            />
        </div>
    ));
};

export const LinearMeter = ({ name, reading = 0, min = 0, max = 100, lastupdated, color = "#4caf50" }) => {
    const percentage = Math.min(100, Math.max(0, ((reading - min) / (max - min)) * 100));

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            background: '#151515',
            padding: '12px 18px',
            borderRadius: '10px',
            border: '1px solid #333',
            minWidth: '220px', // Narrower footprint
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
        }}>
            {/* 1. Slim Vertical Bar */}
            <div style={{
                width: '12px',
                height: '80px',
                background: '#222',
                border: '1px solid #444',
                position: 'relative',
                borderRadius: '10px',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    height: `${percentage}%`,
                    background: color,
                    boxShadow: `0 0 10px ${color}aa`,
                    transition: 'height 1s cubic-bezier(0.4, 0, 0.2, 1)'
                }} />
            </div>

            {/* 2. Content Info */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Asset Name (Top) */}
                <div style={{
                    color: '#888',
                    fontSize: '10px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    marginBottom: '2px'
                }}>
                    {name}
                </div>

                {/* Reading */}
                <div style={{
                    color: '#fff',
                    fontSize: '28px',
                    fontWeight: '300',
                    fontFamily: 'monospace',
                    lineHeight: '1.1'
                }}>
                    {reading}<span style={{ fontSize: '12px', color: '#444', marginLeft: '2px' }}>%</span>
                </div>

                {/* Metadata Stack (Bottom) */}
                <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px'
                }}>
                    <span style={{ color: '#555', fontSize: '9px', fontWeight: 'bold' }}>
                        SCALE: {min}-{max}
                    </span>
                    <span style={{
                        color: color,
                        fontSize: '9px',
                        fontWeight: '600',
                        opacity: 0.8,
                        textTransform: 'uppercase'
                    }}>
                        {lastupdated ? `SYNC: ${lastupdated}` : 'OFFLINE'}
                    </span>
                </div>
            </div>
        </div>
    );
};

export const DigitalMeter = ({ name = '', reading = 0, lastupdated }) => {
    return (
        <div style={{
            background: '#050505',
            padding: '15px 25px',
            borderRadius: '8px',
            border: '2px solid #222',
            display: 'inline-block',
            boxShadow: 'inset 0 0 15px #000',
            textAlign: 'center',
            minWidth: '120px'
        }}>
            <div style={{
                color: '#ff5500',
                fontSize: '32px',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                textShadow: '0 0 8px #ff5500, 0 0 20px #ff0000',
                letterSpacing: '4px'
            }}>
                {reading}
            </div>
            <div style={{
                color: '#444',
                fontSize: '10px',
                letterSpacing: '2px',
                marginTop: '5px',
                fontWeight: 'bold'
            }}>
                {name}
                <div style={{
                    fontSize: '9px',
                    color: '#777',
                    marginTop: '2px',
                    letterSpacing: 'normal',
                    fontWeight: 'normal'
                }}>
                    {lastupdated}
                </div>
            </div>
        </div>
    );
};

export const RadialGauge = ({ name = '', reading = 0, min = 0, max = 100, lastupdated }) => {
    const clampedValue = Math.min(Math.max(reading, min), max);
    const percentage = (clampedValue - min) / (max - min);
    const angle = percentage * 180 - 90;

    const radius = 40;
    const circumference = Math.PI * radius;
    const strokeDashoffset = circumference - percentage * circumference;

    return (
        <div style={{
            width: '90px',
            textAlign: 'center',
            background: 'linear-gradient(145deg, #1e1e1e, #111)',
            padding: '20px 10px 15px 10px',
            borderRadius: '20px',
            border: '1px solid #333',
            boxShadow: '0 10px 20px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.1)',
            fontFamily: 'sans-serif'
        }}>
            <svg viewBox="0 0 100 65" style={{ overflow: 'visible' }}>
                {/* Shadow Drop for depth */}
                <defs>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Gray Background Track */}
                <path
                    d="M10 50 A 40 40 0 0 1 90 50"
                    fill="none" stroke="#2a2a2a" strokeWidth="8" strokeLinecap="round"
                />

                {/* Dynamic Colored Progress Track */}
                <path
                    d="M10 50 A 40 40 0 0 1 90 50"
                    fill="none"
                    stroke={percentage > 0.8 ? "#ff4d4d" : "#00d4ff"}
                    strokeWidth="8"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    filter="url(#glow)"
                    style={{ transition: 'stroke-dashoffset 0.8s ease, stroke 0.5s ease' }}
                />

                {/* Start (Min) Value */}
                <text x="8" y="62" fill="#666" fontSize="5" fontWeight="bold">{min}</text>

                {/* End (Max) Value */}
                <text x="85" y="62" fill="#666" fontSize="5" fontWeight="bold">{max}</text>

                {/* Center Digital Readout */}
                <text x="50" y="48" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" filter="url(#glow)">
                    {clampedValue}
                </text>

                {/* The Needle */}
                <g transform={`rotate(${angle} 50 50)`} style={{ transition: 'transform 0.8s cubic-bezier(0.17, 0.67, 0.83, 0.67)' }}>
                    <line x1="50" y1="50" x2="50" y2="12" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="50" cy="50" r="2.5" fill="#fff" />
                </g>
            </svg>

            <div style={{
                color: '#aaa',
                fontSize: '11px',
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                marginTop: '5px',
                fontWeight: '600'
            }}>
                {name}
                <div style={{
                    fontSize: '9px',
                    color: '#777',
                    marginTop: '2px',
                    textTransform: 'none',
                    letterSpacing: 'normal',
                    fontWeight: '400'
                }}>
                    {lastupdated}
                </div>
            </div>
        </div>
    );
};

export const UptimeCounter = ({ name, lastupdated }) => {

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            background: '#0f1410',
            padding: '12px 18px',
            borderRadius: '8px',
            border: '1px solid #1a2e1c',
            borderLeft: '4px solid #00ff41',
            minWidth: '180px',
            boxShadow: '0 4px 15px rgba(0,255,65,0.1)'
        }}>
            <div style={{
                color: '#5a8a60',
                fontSize: '9px',
                fontWeight: 'bold',
                letterSpacing: '1px'
            }}>
                {name} • LAST CHECKIN
            </div>

            <div style={{
                color: '#00ff41',
                fontSize: '24px',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                marginTop: '4px',
                textShadow: '0 0 10px rgba(0,255,65,0.4)'
            }}>
                {lastupdated}
            </div>

            <div style={{
                color: '#3a4a3c',
                fontSize: '8px',
                marginTop: '8px',
                borderTop: '1px solid #1a2e1c',
                paddingTop: '4px'
            }}>
                STARTED: {lastupdated}
            </div>
        </div>
    );
};

export const renderWidget = (type, name = '', properties, timestamp = '', reading = '', color = '') => {
    const lastupdated = timestamp ? moment.unix(timestamp).fromNow() : '';

    switch (type) {
        case "linearmeter":
            return <LinearMeter name={name} min={properties?.min} max={properties?.max} reading={reading} lastupdated={lastupdated} />;
        case "digitalmeter":
            return <DigitalMeter name={name} reading={reading} lastupdated={lastupdated} color={color} />
        case "radialgauge":
            return <RadialGauge name={name} min={properties?.min} max={properties?.max} reading={reading} lastupdated={lastupdated} color={color} />
        case "indicator":
            return <Indicator name={name} reading={reading} lastupdated={lastupdated} color={color} />
        case "uptimecounter":
            return <UptimeCounter name={name} lastupdated={lastupdated} color={color} />
        default:
            return <RadialGauge name={name} min={properties?.min} max={properties?.max} reading={reading} lastupdated={lastupdated} color={color} />
    }
}
